import React, { useState } from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, Button } from '@mui/material';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BookDemoModal from '../components/BookDemoModal';
import ShieldIcon from '@mui/icons-material/Shield';
import LockIcon from '@mui/icons-material/Lock';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';

import heroDoctorImg from '../assets/hero_doctor.jpg';

const TEAL = '#0B6B6B';
const TEAL_DARK = '#094F4F';
const GREEN = '#25D366';
const NAVY = '#1A2F3A';

export default function SecurityPage() {
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
      <Navbar />

      {/* Hero Banner Section */}
      <Box 
        sx={{ 
          position: 'relative',
          backgroundColor: NAVY,
          color: '#ffffff',
          overflow: 'hidden',
          minHeight: { xs: 'auto', md: '500px' }
        }}
      >
        {/* Desktop absolute background image on the right */}
        <Box
          component="img"
          src={heroDoctorImg}
          alt="Secure Clinical Practice Data"
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute',
            top: 0,
            right: 0,
            height: '100%',
            width: '52%',
            objectFit: 'cover',
            objectPosition: 'center top',
            zIndex: 0,
            maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.15) 15%, black 35%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.15) 15%, black 35%)',
          }}
        />

        {/* Content Container */}
        <Container maxWidth="xl" sx={{ px: { xs: 3, md: 8 }, pt: { xs: 16, md: 22 }, pb: { xs: 8, md: 12 }, position: 'relative', zIndex: 1 }}>
          <Grid container>
            <Grid size={{ xs: 12, md: 6.5 }}>
              {/* Shield badge */}
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, backgroundColor: 'rgba(37,211,102,0.12)', border: '1px solid rgba(37,211,102,0.25)', borderRadius: '50px', px: 2, py: 0.7, mb: 3.5, width: 'fit-content' }}>
                <HealthAndSafetyIcon sx={{ color: GREEN, fontSize: 18 }} />
                <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontWeight: 700, fontSize: '12px', color: 'rgba(255,255,255,0.85)' }}>End-to-End Encrypted Data</Typography>
              </Box>

              <Typography sx={{ fontFamily: 'Outfit, sans-serif', fontSize: { xs: '38px', md: '56px' }, fontWeight: 800, lineHeight: 1.1, mb: 3, letterSpacing: '-1px' }}>
                Bank-Grade Security<br />
                for Your Practice.
              </Typography>
              <Typography sx={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '16px', color: 'rgba(255,255,255,0.65)', mb: 4.5, lineHeight: 1.7, maxWidth: '540px' }}>
                Care Beyond utilizes state-of-the-art encryption protocols, role-based access management, and HIPAA compliant practices to safeguard protected health data.
              </Typography>
              <Button 
                variant="contained" 
                onClick={() => setDemoOpen(true)}
                sx={{ 
                  backgroundColor: TEAL, 
                  color: '#ffffff', 
                  fontFamily: 'Plus Jakarta Sans, sans-serif', 
                  fontWeight: 700, 
                  textTransform: 'none',
                  px: 4.5, 
                  py: 1.6, 
                  borderRadius: '10px', 
                  fontSize: '15px',
                  boxShadow: '0 8px 28px rgba(11,107,107,0.5)',
                  '&:hover': { backgroundColor: TEAL_DARK, transform: 'translateY(-1px)', boxShadow: '0 12px 32px rgba(11,107,107,0.6)' },
                  transition: 'all 0.2s ease'
                }}
              >
                Learn More
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Security Pillars */}
      <Container maxWidth="xl" sx={{ py: 12, px: { xs: 3, md: 8 }, flexGrow: 1 }}>
        <Typography sx={{ fontFamily: 'Outfit, sans-serif', fontSize: { xs: '28px', md: '36px' }, fontWeight: 800, color: NAVY, textAlign: 'center', mb: 1.5 }}>
          Our Security & Compliance Pillars
        </Typography>
        <Typography sx={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '15px', color: '#64748b', textAlign: 'center', mb: 8, maxWidth: '600px', mx: 'auto' }}>
          We treat patient privacy with the highest priority. All exchanges strictly adhere to strict clinical compliance standards.
        </Typography>

        <Grid container spacing={4}>
          {[
            { icon: <ShieldIcon sx={{ fontSize: 28, color: TEAL }} />, title: 'HIPAA Compliant', desc: 'We fully comply with Protected Health Information (PHI) directives, ensuring patient data integrity, privacy, and full transaction logs.' },
            { icon: <LockIcon sx={{ fontSize: 28, color: '#ef4444' }} />, title: 'End-to-End Encryption', desc: 'Communications between patients on WhatsApp and the clinical dashboard are encrypted in transit using SSL/TLS and AES encryption at rest.' },
            { icon: <CloudQueueIcon sx={{ fontSize: 28, color: '#10b981' }} />, title: 'Secure Cloud Infrastructure', desc: 'Hosted on top-tier AWS clouds, using virtual private networks, firewall partitions, and strict database controls to secure clinic storage.' },
            { icon: <CheckCircleIcon sx={{ fontSize: 28, color: '#f59e0b' }} />, title: 'Role-Based Access Control', desc: 'Restrict account actions and permissions to specific doctors or receptionist staff. Audited tracking is compiled for compliance reviews.' }
          ].map((item, idx) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={idx}>
              <Card sx={{ height: '100%', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 12px 30px rgba(0,0,0,0.04)' } }}>
                <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 56, height: 56, borderRadius: '12px', backgroundColor: 'rgba(11,107,107,0.06)' }}>
                    {item.icon}
                  </Box>
                  <Typography sx={{ fontFamily: 'Outfit, sans-serif', fontSize: '18px', fontWeight: 700, color: NAVY }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '14px', color: '#64748b', lineHeight: 1.65 }}>
                    {item.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Footer onOpenDemo={() => setDemoOpen(true)} />
      <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </Box>
  );
}
